import React, { useEffect } from 'react'
import {
  faCheck,
  faCheckDouble,
  faClock,
} from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import momentTZ from 'moment-timezone'
import {
  DcButton,
  DcFlexBox,
  DcInput,
  DcTabs,
  PixelAlert,
  PixelDate,
  PixelDiv,
  PixelFactoryContext,
  PixelHeaderPanel,
  PixelIcon,
  PixelTag,
  PixelText,
  Section,
  colors,
} from 'pixel-base'
import TeamComments from 'pixel-base/components/dc-comments/dc-team-comments'
import { of } from 'rxjs'
import { AjaxResponse } from 'rxjs/ajax'
import styled, { keyframes } from 'styled-components'

import { addNotes, get, post } from 'utils/api-routes.util'

import { useApi } from '../../../../../../shared/hooks/useApi'
import { IPixelFactory, OrderDetail } from '../../../../../decl.d'
import { uploadOrderNoteAttachment } from '../../../../utils/api-routes-orders'
import TestMessages from '../../../admin/pages/SMSSubscription/components/TestMessages'

interface ICommunicationHistory {
  id: number
  ems_customer_id: number
  plan_id: number
  service_id: number
  recipient: string
  text: string
  communication_type: string
  status: string
  error: null
  credits_consumed: number
  priority: number
  processed_at: null
  created_at: CreatedAt
  subject: null
  message: null
  ack_status: null
  customer_feedback: null
}
interface IWhatsappHistory {
  id: number
  ems_customer_id: number
  business_number: number
  customer_number: number
  message_type: string
  message: string
  sent_at: CreatedAt
  delivered_at: CreatedAt
  read_at: CreatedAt
  failed_at: CreatedAt
  received_at: CreatedAt
  created_at: CreatedAt
}
interface IData {
  data: ICommunicationHistory[]
  whatsapp_messages?: IWhatsappHistory[]
}
interface CreatedAt {
  date: Date
  timezone_type: number
  timezone: string
}
interface IConfig {
  REMAINING_BALANCE: number
  CURRENT_PLAN_ID: number
  REMAINING_CREDITS_FOR_PLAN: number
}
interface IProps {
  details: OrderDetail
  detailID: number
  setDetails: (details: OrderDetail) => void
}
const Communication = (props: IProps) => {
  const { details, detailID, setDetails } = props
  const [activeTab, setActiveTab] = React.useState('Order History')
  const [email, setEmail] = React.useState('')
  const [newWhatsAppMessage, setNewWhatsAppMessage] = React.useState('')
  const [clickedMessage, setClickedMessage] = React.useState()
  const [allComments, setAllComments] = React.useState<{
    order_history: any[]
    order_team_comments: any[]
    order_courier_comments: any[]
    sms_comments: any[]
    whatsapp_messages: any[]
  }>({
    order_history: [],
    order_team_comments: [],
    order_courier_comments: [],
    sms_comments: [],
    whatsapp_messages: [],
  })

  const loadingWhatsAppHistory = false

  const [useWhatsapp, setUseWhatsapp] = React.useState(false)
  const [isRecived, setIsRecived] = React.useState(false)
  const [data, setData] = React.useState({
    comment: '',
    note_type: 1,
    order_id: Number(detailID),
  })
  const [config, setConfig] = React.useState<IConfig>({
    REMAINING_BALANCE: 0,
    CURRENT_PLAN_ID: 0,
    REMAINING_CREDITS_FOR_PLAN: 0,
  })

  const { showPageLoader, hidePageLoader, showFailure } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const [popupValue, setPopupValue] = React.useState<any[]>([])
  const [isModalVisible, setIsModalVisible] = React.useState(false)

  const messageInfoRef = React.useRef<HTMLDivElement>(null)
  const prevWhatsAppHistoryRef = React.useRef<any[]>([])
  const chatContainerRef = React.useRef(null)
  const teamCommentsRef = React.useRef<any>(null)
  const commentsTabs = [
    {
      label: 'Order History',
      value: 'Order History',
    },
    {
      label: 'Courier',
      value: 'Courier & System',
    },
    {
      label: 'Team Comments',
      value: 'Team Comments',
    },
    {
      label: 'Communication',
      value: 'Communication',
    },
    {
      label: 'WhatsApp',
      value: 'WhatsApp',
    },
  ]

  const handleReceivedTimeClick = (message) => {
    setClickedMessage(message)
    const popupContent = []
    if (message.message_type === 'send') {
      if (message.failed_at !== null) {
        popupContent.push({ key: 'Failed', value: message.failed_reason })
      } else {
        popupContent.push(
          {
            key: 'Sent',
            value: message.sent_at ? message.sent_at.date : '---',
          },
          {
            key: 'Delivered',
            value: message.delivered_at ? message.delivered_at.date : '---',
          },
          {
            key: 'Read',
            value: message.read_at ? message.read_at.date : '---',
          },
        )
      }
    } else if (message.message_type === 'receive') {
      popupContent.push({
        key: 'Received',
        value: message.received_at ? message.received_at.date : '---',
      })
    }
    setPopupValue(popupContent)
    setIsModalVisible(!isModalVisible)
  }

  function formatDate(dateString) {
    if (dateString !== '---') {
      const date = momentTZ.utc(dateString).tz('Asia/Karachi')

      let formatString
      if (date.isSame(momentTZ().tz('Asia/Karachi'), 'day')) {
        formatString = '[Today] hh:mm A'
      } else if (
        date.isSame(momentTZ().subtract(1, 'days').tz('Asia/Karachi'), 'day')
      ) {
        formatString = '[Yesterday] hh:mm A'
      } else {
        formatString = 'DD/MM/YYYY hh:mm A'
      }

      return moment(dateString).format(formatString)
    }
    return '---'
  }

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }

  const readAndScroll = () => {
    readWhatsappData(detailID)
    scrollToBottom()
  }

  const handleWhatsAppMessageChange = (event) => {
    setNewWhatsAppMessage(event.target.value)
  }

  const handleSendWhatsAppMessage = (event) => {
    event.preventDefault()

    const requestBody = {
      message: newWhatsAppMessage,
    }
    post(
      `/communication/order/${detailID}/wa/send-message`,
      requestBody,
    ).subscribe((res: any) => {
      if (res.response.success) {
        readAndScroll()
      } else {
        showFailure(res.response.error)
      }
      setNewWhatsAppMessage('')
    })
  }

  const readdData = () => {
    get(
      `/order/${detailID}/communication/history`,
      'getCommunicationHistory',
    ).subscribe((res: AjaxResponse<{ response: IData } | any>) => {
      if (res.response?.data) {
        setAllComments({
          ...allComments,
          sms_comments: res.response?.data,
        })
      }
    })
  }

  prevWhatsAppHistoryRef.current = allComments.whatsapp_messages

  const readWhatsappData = (detail_ID: number) => {
    const ID = detail_ID
    get(`/communication/order/${ID}/wa/history`).subscribe(
      (res: AjaxResponse<{ response: IData } | any>) => {
        if (res.response?.data) {
          const newData = res.response.data
          const prevWhatsAppHistory = prevWhatsAppHistoryRef.current
          if (compareData(prevWhatsAppHistory, newData)) {
            setAllComments((prevComments) => ({
              ...prevComments,
              whatsapp_messages: newData || [],
            }))
          }

          if (
            prevWhatsAppHistory &&
            prevWhatsAppHistory.length !== 0 &&
            newData &&
            newData.length !== 0 &&
            compareData(prevWhatsAppHistory, newData)
          ) {
            scrollToBottom()
          }
        }
      },
    )
  }

  const markReadWhatsAppMessages = () => {
    const ID = detailID
    get(`/communication/wa/order/${ID}/read-messages`).subscribe(
      (res: AjaxResponse<{ response: IData } | any>) => {
        if (res.response?.data) {
          readWhatsappData(ID)
        }
      },
    )
  }

  const GetWaSetupInfo = () => {
    get('/communication/wa/setup').subscribe((setupRes: AjaxResponse<any>) => {
      if (setupRes.response?.error) {
        setUseWhatsapp(false)
      } else {
        if (
          setupRes.response?.data?.whatsapp_integrated === 1 &&
          setupRes.response?.data?.auto_notify_whatsapp === 1
        ) {
          setUseWhatsapp(true)
          // Only fetch history if WhatsApp is configured
          readWhatsappData(detailID)
        } else {
          setUseWhatsapp(false)
        }
      }
    })
  }

  const compareData = (previousData: any, latestData: any): boolean => {
    return JSON.stringify(previousData) !== JSON.stringify(latestData)
  }

  const [uploadParams, setUploadParams] = React.useState<{
    file: File
    comment: string
  } | null>(null)

  const { data: uploadData } = useApi(
    () =>
      uploadParams && detailID
        ? uploadOrderNoteAttachment(
            detailID,
            uploadParams.file,
            uploadParams.comment,
          )
        : of(null), // <-- return an Observable, not null
    {
      skip: !uploadParams || !detailID,
      deps: [uploadParams, detailID], // <--- add this line
      onSuccess: (response: any) => {
        hidePageLoader()
        setUploadParams(null) // reset after upload

        // Add the new attachment to the comments list
        if (response?.data) {
          const newAttachment = response.data
          allComments.order_team_comments.push(newAttachment)

          // Update the order details with the new attachment
          setDetails({
            ...details,
            order_notes: [newAttachment, ...details.order_notes],
          })

          // Update the local comments state to reflect the new attachment
          if (
            newAttachment.comment_type_id === 1 ||
            newAttachment.comment_type === 9 ||
            newAttachment.comment_type_id === 9
          ) {
            setAllComments((prev) => ({
              ...prev,
              order_team_comments: [newAttachment, ...prev.order_team_comments],
            }))
          }

          // Switch to Team Comments tab to show the new attachment
          setActiveTab('Team Comments')
        }
      },
    },
  )

  const dateFromNow = (date) => {
    if (!date) return 'Unknown time'
    try {
      return `${moment(date).fromNow()} - ${moment(date).format(
        'DD MMM YYYY hh:mm A',
      )}`
    } catch (error) {
      return 'Invalid date'
    }
  }

  const getFileTypeFromUrl = (url: string) => {
    const extension = url.split('.').pop()?.toLowerCase()
    switch (extension) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg'
      case 'png':
        return 'image/png'
      case 'gif':
        return 'image/gif'
      case 'webp':
        return 'image/webp'
      case 'pdf':
        return 'application/pdf'
      case 'doc':
        return 'application/msword'
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      case 'txt':
        return 'text/plain'
      default:
        return 'application/octet-stream'
    }
  }

  const getAllSmsConfig = () => {
    get('/communication/config').subscribe((res: any) => {
      setConfig({
        REMAINING_BALANCE: res.response.data.balance_available || 0,
        CURRENT_PLAN_ID: res.response.data.current_plan.plan.id,
        REMAINING_CREDITS_FOR_PLAN:
          res.response.data.current_plan.remaining_credit || 0,
      })
    })
  }

  const setIsRecieved = () => {
    if (allComments && allComments.whatsapp_messages) {
      const isRecivedTemp = !!allComments.whatsapp_messages.some(
        (message) => message.message_type === 'receive',
      )
      setIsRecived(isRecivedTemp)
    }
  }
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (useWhatsapp) {
        readWhatsappData(detailID)
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [detailID, activeTab])

  React.useEffect(() => {
    setEmail(details?.shipping?.email)
  }, [details])

  React.useEffect(() => {
    if (activeTab === 'WhatsApp' && !loadingWhatsAppHistory && useWhatsapp) {
      readAndScroll()
      markReadWhatsAppMessages()
    }
  }, [detailID, loadingWhatsAppHistory, activeTab, useWhatsapp])

  React.useEffect(() => {
    if (activeTab === 'WhatsApp' && useWhatsapp) {
      readAndScroll()
    }
  }, [detailID, useWhatsapp])

  React.useEffect(() => {
    GetWaSetupInfo()
    readAndScroll()
    getAllSmsConfig()
  }, [])

  React.useEffect(() => {
    setIsRecieved()
  }, [allComments.whatsapp_messages])

  React.useEffect(() => {
    if (activeTab === 'Communication') {
      if (allComments.sms_comments.length === 0) {
        readdData()
      }
      setData({
        ...data,
        note_type: 7,
      })
    } else if (activeTab === 'WhatsApp') {
      if (allComments.whatsapp_messages.length === 0) {
        readAndScroll()
      }
    } else {
      setData({
        ...data,
        note_type: 1,
        comment: '',
      })
    }
  }, [activeTab, detailID])

  React.useEffect(() => {
    const teamComments: any[] = []
    const courierComments: any[] = []
    const smsComments: any[] = []

    details?.order_notes?.map((note) => {
      if (note.comment_type_id === 1 || note.comment_type_id === 9) {
        teamComments.push(note)
      } else if (note.comment_type_id === 3 || note.comment_type_id === 4) {
        courierComments.push(note)
      } else if (note.comment_type_id === 7) {
        smsComments.push(note)
      }
    }) || []
    setAllComments({
      order_history: details?.order_history || [],
      order_team_comments: teamComments || [],
      order_courier_comments: courierComments || [],
      sms_comments: smsComments || [],
      whatsapp_messages: [],
    })
  }, [details])

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        messageInfoRef.current &&
        !messageInfoRef.current.contains(event.target)
      ) {
        setIsModalVisible(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <>
      <DcFlexBox style={{ marginBottom: '10px' }} flexDirection="column">
        <DcFlexBox
          flexDirection="column"
          height="auto"
          alignContent="center"
          padding="10px 0px"
          style={{ borderBottom: `1px solid ${colors.grayBorderLight}` }}
        >
          <PixelHeaderPanel alignContent="center">
            <PixelText
              textSize="16px"
              color="rgba(0, 0, 0, 0.84)"
              style={{ fontWeight: '600', padding: '10px' }}
            >
              Timeline
            </PixelText>
          </PixelHeaderPanel>
        </DcFlexBox>
        <PixelDiv padding="10px">
          <DcFlexBox width="100%" flexDirection="column" height="auto">
            <TabsContainer>
              <DcTabs
                size="md"
                variant="outline-pills"
                tabs={commentsTabs}
                activeTab={activeTab}
                handleChange={(value) => {
                  setActiveTab(value)
                }}
              />
            </TabsContainer>
            <DcFlexBox flexDirection="column" height="auto" padding="`G0px">
              {activeTab === 'Team Comments' && (
                <>
                  <TeamComments
                    comments={(() => {
                      const filteredComments =
                        allComments.order_team_comments.filter(
                          (note) =>
                            note &&
                            (note.order_comment || note.comment_id) &&
                            typeof note === 'object',
                        )

                      const mappedComments = filteredComments.map((note) => {
                        const mappedComment = {
                          id:
                            note.comment_id?.toString() ||
                            note.id?.toString() ||
                            Math.random().toString(),
                          author: {
                            name: String(
                              note.name || note.fm_user_id || 'Unknown User',
                            ),
                            username: String(
                              (note.name || note.fm_user_id || 'unknown')
                                ?.toLowerCase()
                                .replace(/\s+/g, '.') || 'unknown.user',
                            ),
                            role: String(note.role || 'Team Member'),
                          },
                          content: String(note.order_comment || ''),
                          timestamp: String(
                            note.comment_time
                              ? dateFromNow(note.comment_time)
                              : 'Unknown time',
                          ),
                          likes: 0,
                          isLiked: false,
                          attachments: note.attachment
                            ? [
                                {
                                  id:
                                    note.attachment.attachment_id?.toString() ||
                                    Math.random().toString(),
                                  name:
                                    note.attachment.attachment_original_name ||
                                    note.attachment.attachment_name ||
                                    'Attachment',
                                  size: note.attachment.attachment_size || 0,
                                  type:
                                    note.attachment.attachment_mime_type ||
                                    'application/octet-stream',
                                  url: note.attachment.attachment_path || '',
                                },
                              ]
                            : note.attachment_path
                            ? [
                                {
                                  id:
                                    note.comment_id?.toString() ||
                                    Math.random().toString(),
                                  name:
                                    note.attachment_path.split('/').pop() ||
                                    'Attachment',
                                  size: 0,
                                  type: getFileTypeFromUrl(
                                    note.attachment_path,
                                  ),
                                  url: note.attachment_path,
                                },
                              ]
                            : undefined,
                        }
                        return mappedComment
                      })
                      return mappedComments
                    })()}
                    onAddComment={(content, attachments) => {
                      if (attachments && attachments.length > 0) {
                        // If there are attachments, use the attachment endpoint
                        showPageLoader()

                        // Use the uploadOrderNoteAttachment function with the first attachment
                        uploadOrderNoteAttachment(
                          detailID,
                          attachments[0], // Use first attachment only
                          content,
                        ).subscribe((res: AjaxResponse<any>) => {
                          hidePageLoader()
                          if (res.response.data) {
                            // Don't update details immediately to avoid triggering useEffect
                            // setDetails({
                            //   ...details,
                            //   order_notes: [
                            //     res.response.data,
                            //     ...details.order_notes,
                            //   ],
                            // })

                            // Update the local comments state to reflect the new note
                            const structuredData = {
                              comment_id: res.response.data.comment_id,
                              order_id: res.response.data.order_id,
                              fm_user_id: res.response.data.fm_user_id,
                              name: res.response.data.name,
                              order_comment: res.response.data.order_comment,
                              comment_type_id:
                                res.response.data.comment_type_id,
                              comment_type: res.response.data.comment_type,
                              comment_time: res.response.data.comment_time,
                              // Handle attachment data - check if it's in attachment object or attachment_path
                              attachment: res.response.data.attachment || null,
                              attachment_path:
                                res.response.data.attachment_path || null,
                            }
                            const newNote = structuredData

                            setAllComments((prev) => {
                              const updatedComments = {
                                ...prev,
                                order_team_comments: [
                                  newNote,
                                  ...prev.order_team_comments,
                                ],
                              }

                              return updatedComments
                            })
                            if (
                              newNote.comment_type_id === 3 ||
                              newNote.comment_type_id === 4
                            ) {
                              setAllComments((prev) => ({
                                ...prev,
                                order_courier_comments: [
                                  newNote,
                                  ...prev.order_courier_comments,
                                ],
                              }))
                            } else if (newNote.comment_type_id === 7) {
                              setAllComments((prev) => ({
                                ...prev,
                                sms_comments: [newNote, ...prev.sms_comments],
                              }))
                            }

                            setActiveTab('Team Comments')
                            setData({
                              comment: '',
                              note_type: 1,
                              order_id: detailID,
                            })
                          }
                        })
                      } else {
                        // Regular comment without attachment - pass content directly
                        const commentData = {
                          ...data,
                          comment: content,
                          note_type: 1,
                        }

                        showPageLoader()
                        addNotes(detailID, commentData).subscribe(
                          (res: AjaxResponse<any>) => {
                            hidePageLoader()
                            if (res.response.data) {
                              // Don't update details immediately to avoid triggering useEffect
                              // setDetails({
                              //   ...details,
                              //   order_notes: [
                              //     res.response.data,
                              //     ...details.order_notes,
                              //   ],
                              // })

                              // Update the local comments state to reflect the new note
                              const newNote = res.response.data
                              if (newNote.comment_type_id === 1) {
                                setAllComments((prev) => ({
                                  ...prev,
                                  order_team_comments: [
                                    newNote,
                                    ...prev.order_team_comments,
                                  ],
                                }))
                              } else if (
                                newNote.comment_type_id === 3 ||
                                newNote.comment_type_id === 4
                              ) {
                                setAllComments((prev) => ({
                                  ...prev,
                                  order_courier_comments: [
                                    newNote,
                                    ...prev.order_courier_comments,
                                  ],
                                }))
                              } else if (newNote.comment_type_id === 7) {
                                setAllComments((prev) => ({
                                  ...prev,
                                  sms_comments: [newNote, ...prev.sms_comments],
                                }))
                              }

                              setActiveTab('Team Comments')
                              setData({
                                comment: '',
                                note_type: 1,
                                order_id: detailID,
                              })
                            }
                          },
                        )
                      }
                    }}
                    showAddComment={true}
                    currentUser={{
                      name: 'Current User', // Replace with actual current user name
                      avatar: undefined, // Replace with actual current user avatar
                    }}
                  />
                </>
              )}
              {activeTab === 'Order History' && (
                <>
                  <DcFlexBox height="auto" padding="10px 10px 0px">
                    <PixelText
                      textSize="20px"
                      style={{ fontWeight: '600', marginBottom: '10px' }}
                    >
                      Order update history
                    </PixelText>
                  </DcFlexBox>
                  <Section noPadding>
                    {allComments?.order_history?.length ? (
                      <>
                        {allComments?.order_history.map((note, index) => (
                          <DcFlexBox
                            position="relative"
                            height="auto"
                            key={index}
                            className={note?.is_new ? 'new' : ''}
                          >
                            <DcFlexBox
                              flexDirection="column"
                              justifyContent="baseline"
                              flexWrap="nowrap"
                              alignItems="baseline"
                              alignContent="flex-start"
                              width="100%"
                              margin="0px 0px 30px 0px"
                              padding="15px"
                              style={{
                                backgroundColor: colors.white,
                                boxShadow: '0px 0px 5px rgb(0 0 0 / 10%)',
                                borderRadius: '5px',
                                zIndex: 5,
                              }}
                            >
                              <DcFlexBox
                                justifyContent="baseline"
                                alignItems="center"
                                alignContent="center"
                                height="auto"
                              >
                                <PixelText
                                  textSize="16px"
                                  color="rgba(0, 0, 0, 0.84)"
                                  style={{ fontWeight: '500' }}
                                >
                                  {note?.user_name}
                                </PixelText>
                                <PixelText
                                  variant="light"
                                  textSize="11px"
                                  color="rgba(0, 0, 0, 0.84)"
                                  style={{ marginLeft: '5px' }}
                                >
                                  {dateFromNow(note?.update_time)}
                                </PixelText>
                              </DcFlexBox>
                              <PixelText
                                textSize="15px"
                                color="rgba(0, 0, 0, 0.84)"
                                style={{ fontWeight: '400' }}
                              >
                                {note?.message}
                              </PixelText>
                            </DcFlexBox>
                            {index + 1 ===
                            allComments?.order_history?.length ? (
                              <NotBefore />
                            ) : (
                              <Before />
                            )}
                          </DcFlexBox>
                        ))}
                      </>
                    ) : (
                      <PixelDiv
                        style={{
                          padding: '20px 2px',
                        }}
                      >
                        <PixelAlert variant="info">No Comments</PixelAlert>
                      </PixelDiv>
                    )}
                  </Section>
                </>
              )}
              {activeTab === 'Courier & System' && (
                <>
                  <DcFlexBox height="auto" padding="10px 10px 0px">
                    <PixelText
                      textSize="20px"
                      style={{ fontWeight: '600', marginBottom: '10px' }}
                    >
                      Courier & system updates
                    </PixelText>
                  </DcFlexBox>
                  <Section noPadding>
                    {allComments?.order_courier_comments?.length ? (
                      <>
                        {allComments?.order_courier_comments.map(
                          (note, index) => {
                            return (
                              <DcFlexBox
                                position="relative"
                                height="auto"
                                key={index}
                                className={note.is_new ? 'new' : ''}
                              >
                                <DcFlexBox
                                  flexDirection="column"
                                  justifyContent="baseline"
                                  flexWrap="nowrap"
                                  alignItems="baseline"
                                  alignContent="flex-start"
                                  width="100%"
                                  margin="0px 0px 30px 0px"
                                  padding="15px"
                                  style={{
                                    backgroundColor: colors.white,
                                    boxShadow: '0px 0px 5px rgb(0 0 0 / 10%)',
                                    borderRadius: '5px',
                                    zIndex: 5,
                                  }}
                                >
                                  <DcFlexBox
                                    justifyContent="baseline"
                                    alignItems="center"
                                    alignContent="center"
                                    height="auto"
                                  >
                                    <PixelText
                                      textSize="16px"
                                      color="rgba(0, 0, 0, 0.84)"
                                      style={{ fontWeight: '500' }}
                                    >
                                      {note?.name}
                                    </PixelText>
                                    <PixelText
                                      variant="light"
                                      textSize="11px"
                                      color="rgba(0, 0, 0, 0.84)"
                                      style={{ marginLeft: '5px' }}
                                    >
                                      {dateFromNow(note.comment_time)}
                                    </PixelText>
                                  </DcFlexBox>
                                  <PixelText
                                    textSize="15px"
                                    color="rgba(0, 0, 0, 0.84)"
                                    style={{ fontWeight: '400' }}
                                  >
                                    {note?.order_comment}
                                  </PixelText>
                                </DcFlexBox>
                                {index + 1 ===
                                allComments?.order_courier_comments.length ? (
                                  <NotBefore />
                                ) : (
                                  <Before />
                                )}
                              </DcFlexBox>
                            )
                          },
                        )}
                      </>
                    ) : (
                      <PixelDiv
                        style={{
                          padding: '20px 2px',
                        }}
                      >
                        <PixelAlert variant="info">No Comments</PixelAlert>
                      </PixelDiv>
                    )}
                  </Section>
                </>
              )}
              {activeTab === 'Communication' && (
                <>
                  <TestMessages
                    orderId={detailID}
                    phone={details?.shipping?.phone || ''}
                    email={email || ''}
                    planId={config?.CURRENT_PLAN_ID}
                    SMSConfig={config}
                    isItOrder={true}
                    data={data}
                    setData={setData}
                    handleUpdate={(data: ICommunicationHistory) => {
                      if (data)
                        setAllComments({
                          ...allComments,
                          sms_comments: [data, ...allComments.sms_comments],
                        })
                    }}
                  />
                  <DcFlexBox height="auto" padding="10px 10px 0px">
                    <PixelText
                      textSize="20px"
                      style={{ fontWeight: '600', marginBottom: '10px' }}
                    >
                      History
                    </PixelText>
                  </DcFlexBox>
                  <Section noPadding>
                    {allComments?.sms_comments?.length ? (
                      <>
                        {allComments?.sms_comments.map(
                          (note: ICommunicationHistory, index) => {
                            return (
                              <DcFlexBox
                                position="relative"
                                height="auto"
                                key={index}
                              >
                                <DcFlexBox
                                  flexDirection="row"
                                  justifyContent="baseline"
                                  flexWrap="nowrap"
                                  alignItems="baseline"
                                  alignContent="flex-start"
                                  width="100%"
                                  margin="0px 0px 30px 0px"
                                  padding="15px"
                                  style={{
                                    backgroundColor: colors.white,
                                    boxShadow: '0px 0px 5px rgb(0 0 0 / 10%)',
                                    borderRadius: '5px',
                                    zIndex: 5,
                                  }}
                                >
                                  <DcFlexBox
                                    justifyContent="baseline"
                                    alignItems="flex-start"
                                    alignContent="center"
                                    height="auto"
                                    flexDirection="column"
                                    width="60%"
                                  >
                                    <PixelTag>{note.status}</PixelTag>
                                    <PixelText textSize="12px">
                                      Recipient:{' '}
                                      <strong>{note.recipient}</strong>
                                    </PixelText>
                                    <PixelText textSize="12px">
                                      Type:
                                      <strong>
                                        {' '}
                                        {note.communication_type}
                                      </strong>
                                    </PixelText>
                                    {note.communication_type !== 'call' && (
                                      <PixelText textSize="12px">
                                        Text:<strong> {note.text}</strong>
                                      </PixelText>
                                    )}
                                    {note.message !== null && (
                                      <PixelText textSize="12px">
                                        Message:
                                        <strong
                                          style={{
                                            color: `${
                                              note.status == 'failed'
                                                ? 'red'
                                                : 'black'
                                            }`,
                                          }}
                                        >
                                          {note.message}
                                        </strong>
                                      </PixelText>
                                    )}
                                  </DcFlexBox>
                                  <DcFlexBox
                                    justifyContent="baseline"
                                    alignItems="flex-end"
                                    alignContent="center"
                                    height="auto"
                                    flexDirection="column"
                                    width="40%"
                                  >
                                    <PixelDate
                                      value={note.created_at.date}
                                      format="pixelStandard"
                                    />
                                  </DcFlexBox>
                                </DcFlexBox>
                                {index + 1 ===
                                allComments?.sms_comments.length ? (
                                  <NotBefore />
                                ) : (
                                  <Before />
                                )}
                              </DcFlexBox>
                            )
                          },
                        )}
                      </>
                    ) : (
                      <PixelDiv
                        style={{
                          padding: '20px 2px',
                        }}
                      >
                        <PixelAlert variant="info">No SMS Sent</PixelAlert>
                      </PixelDiv>
                    )}
                  </Section>
                </>
              )}
              {activeTab === 'WhatsApp' && (
                <>
                  {loadingWhatsAppHistory ? (
                    <PixelDiv
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                      }}
                    >
                      <PixelDiv
                        style={{
                          border: `4px solid ${colors.grayBorderLight}`,
                          borderTop: `4px solid ${colors.primary}`,
                          borderRadius: '50%',
                          width: '30px',
                          height: '30px',
                          animation: `${spinAnimation} 2s linear infinite`,
                        }}
                      />
                    </PixelDiv>
                  ) : allComments.whatsapp_messages.length ? (
                    <PixelDiv>
                      <PixelDiv
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          maxHeight: '400px',
                          overflowY: 'auto',
                        }}
                        ref={chatContainerRef}
                      >
                        {allComments.whatsapp_messages.map((message, index) => (
                          <React.Fragment key={index}>
                            <DcFlexBox
                              style={{
                                position: 'relative',
                                flexDirection: 'column',
                                alignItems:
                                  message.message_type === 'send'
                                    ? 'flex-end'
                                    : 'flex-start',
                              }}
                              key={index}
                              type={message.message_type}
                            >
                              <PixelDiv
                                style={{
                                  backgroundColor:
                                    message.message_type === 'send'
                                      ? colors.successLight
                                      : colors.grayBorderLight,
                                  width: 'fit-content',
                                  padding: '8px 12px',
                                  borderRadius: '10px',
                                  margin: '4px',
                                  flexDirection: 'column',
                                  display: 'flex',
                                  alignItems:
                                    message.message_type === 'send'
                                      ? 'flex-end'
                                      : 'flex-start',
                                }}
                              >
                                <PixelText
                                  style={{
                                    fontSize: '16px',
                                    alignSelf:
                                      message.message_type === 'send'
                                        ? 'flex-end'
                                        : 'flex-start',
                                    textAlign:
                                      message.message_type === 'send'
                                        ? 'right'
                                        : 'left',
                                    marginBottom: '4px',
                                    color: colors.grayTextMedium,
                                  }}
                                >
                                  {message.message}
                                </PixelText>
                                <PixelText
                                  style={{
                                    fontSize: '12px',
                                    color: colors.grayTextMedium,
                                    textAlign:
                                      message.message_type === 'send'
                                        ? 'right'
                                        : 'left',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() =>
                                    handleReceivedTimeClick(message)
                                  }
                                >
                                  {message.message_type === 'receive'
                                    ? message.received_at
                                      ? momentTZ
                                          .utc(message.received_at.date)
                                          .tz('Asia/Karachi')
                                          .format('LT')
                                      : momentTZ
                                          .utc(message.created_at.date)
                                          .tz('Asia/Karachi')
                                          .format('LT')
                                    : message.sent_at
                                    ? momentTZ
                                        .utc(message.sent_at.date)
                                        .tz('Asia/Karachi')
                                        .format('LT')
                                    : momentTZ
                                        .utc(message.created_at.date)
                                        .tz('Asia/Karachi')
                                        .format('LT')}{' '}
                                  <PixelIcon
                                    icon={
                                      message.failed_at !== null
                                        ? faClock
                                        : message.read_at !== null
                                        ? faCheckDouble
                                        : message.delivered_at !== null
                                        ? faCheckDouble
                                        : message.sent_at !== null
                                        ? faCheck
                                        : null
                                    }
                                    color={
                                      message.failed_at !== null
                                        ? 'red'
                                        : message.read_at !== null
                                        ? 'blue'
                                        : message.delivered_at !== null
                                        ? 'gray'
                                        : message.sent_at !== null
                                        ? 'gray'
                                        : 'gray'
                                    }
                                    style={{ cursor: 'pointer' }}
                                    onClick={() =>
                                      handleReceivedTimeClick(message)
                                    }
                                  />
                                </PixelText>
                              </PixelDiv>
                              {isModalVisible && clickedMessage === message && (
                                <PixelDiv
                                  ref={messageInfoRef}
                                  style={{
                                    backgroundColor: colors.white,
                                    boxShadow: '0 0 5px rgba(0, 0, 0, 0.1)',
                                    width: '255px',
                                    height: 'fit-content',
                                    position: 'absolute',
                                    borderRadius: '10px',
                                    zIndex: '55',
                                    padding: '5px',
                                    top: '93%',
                                    right:
                                      message.message_type === 'send'
                                        ? '6%'
                                        : '60%',
                                  }}
                                >
                                  {popupValue.map((item, i) => {
                                    return (
                                      <PixelDiv
                                        key={i}
                                        style={{
                                          textAlign: 'left',
                                          display: 'flex',
                                          gap: '5px',
                                          justifyContent: 'space-between',
                                        }}
                                      >
                                        <PixelDiv
                                          style={{
                                            width: '40%',
                                            display: 'flex',
                                            gap: '10px',
                                          }}
                                        >
                                          <PixelIcon
                                            icon={
                                              item.key == 'Read'
                                                ? faCheckDouble
                                                : item.key == 'Delivered'
                                                ? faCheckDouble
                                                : item.key == 'Sent'
                                                ? faCheck
                                                : item.key == 'Failed'
                                                ? faClock
                                                : null
                                            }
                                            color={
                                              item.key == 'Read'
                                                ? 'blue'
                                                : item.key == 'Delivered'
                                                ? 'gray'
                                                : item.key == 'Sent'
                                                ? 'gray'
                                                : item.key == 'Failed'
                                                ? 'red'
                                                : 'gray'
                                            }
                                            style={{
                                              cursor: 'pointer',
                                            }}
                                            onClick={() =>
                                              handleReceivedTimeClick(message)
                                            }
                                          />

                                          <PixelDiv
                                            style={{
                                              fontWeight: 'bold',
                                              color: colors.purple,
                                              textAlign: 'left',
                                              fontSize: '12px',
                                            }}
                                          >
                                            {item.key}
                                          </PixelDiv>
                                        </PixelDiv>

                                        <PixelDiv
                                          style={{
                                            textAlign: 'center',
                                            fontSize: '12px',
                                            width: '60%',
                                          }}
                                        >
                                          {!message.failed_at
                                            ? formatDate(item.value)
                                            : item.value}
                                        </PixelDiv>
                                      </PixelDiv>
                                    )
                                  })}
                                </PixelDiv>
                              )}
                            </DcFlexBox>

                            <PixelDiv
                              style={{
                                position: 'relative',
                                display: 'flex',
                                justifyContent:
                                  message.message_type == 'send'
                                    ? 'flex-end'
                                    : 'flex-start',
                                marginTop: '-5px',
                                marginRight: '7px',
                              }}
                            >
                              {message.reaction && (
                                <PixelText
                                  style={{
                                    backgroundColor: colors.white,
                                    color: colors.grayTextDark,
                                    fontSize: '12px',
                                    padding: '2px 2px',
                                    borderRadius: '5px',
                                  }}
                                >
                                  {message.reaction}
                                </PixelText>
                              )}
                            </PixelDiv>
                          </React.Fragment>
                        ))}
                      </PixelDiv>
                    </PixelDiv>
                  ) : (
                    <PixelDiv
                      style={{
                        padding: '20px 2px',
                      }}
                    >
                      <PixelAlert variant="info">
                        No WhatsApp Messages
                      </PixelAlert>
                    </PixelDiv>
                  )}

                  <DcFlexBox
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 20,
                    }}
                  >
                    <>
                      <DcInput
                        type="text"
                        placeholder={
                          isRecived
                            ? 'Type a message...'
                            : 'Since you have no received messages, you cannot send custom message...'
                        }
                        value={newWhatsAppMessage}
                        onChange={handleWhatsAppMessageChange}
                        disabled={!isRecived}
                      />
                      &nbsp;&nbsp;
                      <DcButton
                        onClick={handleSendWhatsAppMessage}
                        disabled={!newWhatsAppMessage}
                        style={{
                          backgroundColor: !newWhatsAppMessage ? 'gray' : '',
                          cursor: 'pointer',
                        }}
                      >
                        Send
                      </DcButton>
                    </>
                  </DcFlexBox>
                </>
              )}
            </DcFlexBox>
          </DcFlexBox>
        </PixelDiv>
      </DcFlexBox>
    </>
  )
}
const Before = styled.div`
  border-left: 5px solid ${colors.grayBorderMedium};
  position: absolute;
  left: 20px;
  bottom: 0px;
  content: '';
  display: block;
  width: auto;
  height: 30px;
  z-index: 1;
`
const NotBefore = styled.div`
  border-left: 0;
  position: absolute;
  left: 0;
  top: 0;
  content: '';
  display: block;
  width: auto;
  height: 0;
  position: absolute;
  z-index: 0;
`

const PostButton = styled.div`
  position: absolute;
  right: 4px;
  top: 15px;
`
const AttachmentButton = styled.div`
  position: absolute;
  right: 60px;
  top: 15px;
`
const TabsContainer = styled.div``

const FormGroup = styled.div<{ flexEnd?: boolean }>`
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: ${(props) => (props.flexEnd ? 'flex-end' : 'flex-start')};
  align-items: flex-start;
  align-content: flex-start;
  text-align: center;
  width: 100%;
  flex-grow: 1;
  gap: 10px;
  margin-bottom: 10px;
  .full-width {
    flex-grow: 1;
  }
`
const spinAnimation = keyframes`
to {
  transform: rotate(360deg);
}
`

export default Communication
